<template>
  <section class="page">
    <header class="header">
      <div class="header-left">
        <h1 class="title">Listado de usuarios</h1>
        <p class="subtitle">
          Vista de solo lectura de la base de datos:
          <strong>formatos-compromisos / usuarios</strong>
        </p>
      </div>

      <div class="header-right">
        <button
          class="btn-download"
          @click="handleDownloadExcel"
          :disabled="downloading"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            class="icon-excel"
          >
            <path
              d="M4 4h10l6 6v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"
            />
            <path
              fill="#fff"
              d="M8.3 15.5 10 13l1.7 2.5h1.9l-2.3-3.3 2.2-3.2H11L9.3 11 7.6 8.5H5.7L8 11.7 5.6 15.5h1.9z"
            />
          </svg>
          <span v-if="!downloading">Descargar Excel</span>
          <span v-else>Generando...</span>
        </button>
      </div>
    </header>

    <section class="table-wrapper">
      <UsuariosTable
        :rows="usuarios"
        :loading="loading"
        :error="error"
      />
    </section>
  </section>
</template>

<script setup>
import { ref, onMounted } from "vue";
import Swal from "sweetalert2";
import UsuariosTable from "../components/UsuariosTable.vue";
import { getUsuarios, downloadExcel } from "../services/api.js";

const ERROR_MSG_CARGA = "No se pudo cargar la información.";
const EXCEL_FILENAME = "usuarios-formato-compromiso.xlsx";

const usuarios = ref([]);
const loading = ref(false);
const error = ref(null);
const downloading = ref(false);

const fetchUsuarios = async () => {
  loading.value = true;
  error.value = null;
  try {
    const res = await getUsuarios();
    usuarios.value = res.data?.data || [];
  } catch (err) {
    console.error(err);
    error.value = ERROR_MSG_CARGA;
  } finally {
    loading.value = false;
  }
};

const handleDownloadExcel = async () => {
  try {
    downloading.value = true;

    Swal.fire({
      title: "Generando archivo...",
      text: "Por favor espera mientras preparamos la descarga.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    const blob = await downloadExcel();

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = EXCEL_FILENAME;
    document.body.appendChild(a);
    a.click();

    a.remove();
    window.URL.revokeObjectURL(url);

    Swal.fire({
      icon: "warning",
      title: "Descarga completada",
      html: `
        <p style="margin-bottom: 0.75rem;">El archivo se descargó correctamente.</p>
        <hr style="border: none; border-top: 1px solid #e5e7eb; margin: 0.75rem 0;" />
        <p style="font-size: 0.9rem; color: #92400e; margin: 0;">
          ⚠️ Los enlaces a los formatos compromiso tienen una vigencia de
          <strong>3 días</strong>. Si al abrir el archivo ves que las imágenes
          no cargan, descarga nuevamente el Excel para refrescar los enlaces.
        </p>
      `,
      confirmButtonText: "OK",
      confirmButtonColor: "#D97706",
      customClass: { popup: "swal-success" },
    });
  } catch (err) {
    console.error("Error descargando Excel:", err);
    Swal.fire({
      icon: "error",
      title: "Error en la descarga",
      text: "No se pudo descargar el archivo. Intenta nuevamente.",
      confirmButtonText: "Entendido",
      confirmButtonColor: "#d33",
    });
  } finally {
    downloading.value = false;
  }
};

onMounted(() => {
  fetchUsuarios();
});
</script>

<style scoped>
.page {
  max-width: 1200px;
  margin: 0 auto;
  background-color: #ffffff;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 16px 32px rgba(0, 0, 0, 0.05);
  border: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.header {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 1rem;
}

.header-left {
  flex: 1;
  min-width: 260px;
}

.title {
  font-size: 1.35rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
  line-height: 1.3;
}

.subtitle {
  font-size: 0.9rem;
  color: #555;
  margin-top: 0.25rem;
  line-height: 1.4;
}

.subtitle strong {
  color: #1b5e20;
  font-weight: 600;
}

.header-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.btn-download {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  appearance: none;
  border: 0;
  border-radius: 8px;
  background-color: #1b5e20;
  color: #fff;
  font-size: 0.9rem;
  font-weight: 500;
  line-height: 1;
  padding: 0.8rem 1.2rem;
  cursor: pointer;
  min-width: 170px;
  transition: background-color 0.2s ease, transform 0.1s ease;
  box-shadow: 0 3px 10px rgba(27, 94, 32, 0.25);
}

.btn-download:hover:not(:disabled) {
  background-color: #2e7d32;
  transform: translateY(-1px);
}

.btn-download:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.icon-excel {
  width: 18px;
  height: 18px;
  fill: currentColor;
}

.table-wrapper {
  border: 1px solid #e5e5e5;
  border-radius: 12px;
  background-color: #fff;
  padding: 0;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}

.swal-success {
  border-radius: 12px !important;
  font-family: "Inter", sans-serif;
}
</style>
